import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import InvestmentDeclarationSettings from "../InvestmentDeclarationSettings";
import ReimbursementSettings from "./ReimbursementSettings";
import PayrollStepsSettingsTab from "./PayrollStepsSettingsTab";
import EnterprisePayrollDefaultsRows from "./EnterprisePayrollDefaultsRows";
import PayrollGeneralSettingRow from "./PayrollGeneralSettingRow";
import EnhancedSalaryStructureManager from "../employee-list/EmployeeSalaryStructureManager.jsx";
import StatutoryRatesHeadsPanel from "./StatutoryRatesHeadsPanel.jsx";
import PayrollPoliciesTab from "./PayrollPoliciesTab";
import { payrollService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import {
  readCompanyDataFromStorage,
} from "./taxYearUtils";
import usePolicyCompanyRoles from "../policy-wizard/usePolicyCompanyRoles";

const PAYROLL_SETTINGS_SUBS = [
  "general",
  "salary_structures",
  "statutory_rates_heads",
  "declaration",
  "reimbursements",
  "policies",
  "payroll_setup",
  "tds_compliance",
];

/**
 * Embedded Payroll Settings page (not a modal).
 * Point Payroll Settings feature work at this module.
 */
export default function PayrollSettingsPage({
  darkMode = false,
  employees = [],
  payrollSettings = null,
  investmentSettingsRef = null,
  onFlagsChange,
  onSettingsSaved,
  canEdit = true,
  initialSub = null,
  departmentsList = [],
  branchesList = [],
  designationsList = [],
  subDepartmentsList = [],
  gradesList = [],
  categoriesList = [],
}) {
  const { hasScreenAccess, isAdmin } = usePermissions() || {};
  const screenOk = (screen, action = "view") =>
    isAdmin ||
    typeof hasScreenAccess !== "function" ||
    hasScreenAccess("payroll", screen, action) ||
    (action === "view" &&
      (hasScreenAccess("payroll", screen, "edit") ||
        hasScreenAccess("payroll", screen, "create")));

  const canViewGeneral = screenOk("payroll_settings");
  const canEditGeneral =
    canEdit &&
    (isAdmin ||
      typeof hasScreenAccess !== "function" ||
      hasScreenAccess("payroll", "payroll_settings", "edit"));
  const canViewSalaryStructures = screenOk("salary_structure");
  const canViewStatutory =
    screenOk("statutory_rates") ||
    screenOk("tds_reference_heads") ||
    screenOk("tds_engine_compliance");
  const canViewDeclaration = screenOk("payroll_settings");
  const canViewReimbursements =
    screenOk("flexi_benefits") || screenOk("payroll_settings");
  const canEditReimbursements =
    canEdit &&
    (isAdmin ||
      typeof hasScreenAccess !== "function" ||
      hasScreenAccess("payroll", "flexi_benefits", "edit") ||
      hasScreenAccess("payroll", "payroll_settings", "edit"));
  const canViewPolicies =
    screenOk("loan_policies") || screenOk("leave_encashment_policies");
  const canViewPayrollSetup = screenOk("payroll_settings");
  const canViewTdsHeads =
    typeof hasScreenAccess !== "function" ||
    hasScreenAccess("payroll", "tds_reference_heads", "view") ||
    hasScreenAccess("payroll", "salary_structure", "view");
  const canViewTdsCompliance =
    typeof hasScreenAccess !== "function" ||
    hasScreenAccess("payroll", "tds_engine_compliance", "view");

  const tabAllowed = {
    general: canViewGeneral,
    salary_structures: canViewSalaryStructures,
    statutory_rates_heads: canViewStatutory,
    declaration: canViewDeclaration,
    reimbursements: canViewReimbursements,
    policies: canViewPolicies,
    payroll_setup: canViewPayrollSetup,
  };

  // Legacy "tds_compliance" deep-links now open Statutory Rates → TDS FVU Versions.
  const normalizeSub = (sub) => (sub === "tds_compliance" ? "statutory_rates_heads" : sub);
  const firstAllowedTab =
    PAYROLL_SETTINGS_SUBS.map(normalizeSub).find((k) => tabAllowed[k]) || "general";
  const [tab, setTab] = useState(() => {
    const wanted = PAYROLL_SETTINGS_SUBS.includes(initialSub)
      ? normalizeSub(initialSub)
      : "general";
    return tabAllowed[wanted] ? wanted : firstAllowedTab;
  });
  const initialAct = initialSub === "tds_compliance" ? "fvu" : null;
  const [saving, setSaving] = useState(false);
  const [savingRow, setSavingRow] = useState(null);
  const [allowMidMonthIncrements, setAllowMidMonthIncrements] = useState(false);
  const [attendanceDependencyMode, setAttendanceDependencyMode] = useState("based");
  const [independentAttendanceFeed, setIndependentAttendanceFeed] = useState("daywise_import");
  const [arrearsMode, setArrearsMode] = useState("merge_single");
  const [arrearsMergedName, setArrearsMergedName] = useState("Arrears");
  const [arrearsEffectiveFrom, setArrearsEffectiveFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [arrearsHistory, setArrearsHistory] = useState([]);
  const [notifyOnFinalise, setNotifyOnFinalise] = useState(false);
  const [notifyOnUnfinalise, setNotifyOnUnfinalise] = useState(false);
  const [notifyRoleCodes, setNotifyRoleCodes] = useState([]);
  const { policyRoleOptions } = usePolicyCompanyRoles();
  const localInvRef = useRef(null);
  const invRef = investmentSettingsRef || localInvRef;
  const reimbRef = useRef(null);

  const companyData = useMemo(() => readCompanyDataFromStorage(), []);

  useEffect(() => {
    if (PAYROLL_SETTINGS_SUBS.includes(initialSub)) {
      const wanted = normalizeSub(initialSub);
      setTab(tabAllowed[wanted] ? wanted : firstAllowedTab);
      return;
    }
    if (!tabAllowed[tab]) setTab(firstAllowedTab);
  }, [initialSub, firstAllowedTab]);

  useEffect(() => {
    invRef.current?.loadFromSettings?.(payrollSettings);
    reimbRef.current?.loadFromSettings?.(payrollSettings);
  }, [payrollSettings]);

  useEffect(() => {
    setAllowMidMonthIncrements(!!payrollSettings?.allowMidMonthIncrements);
    setAttendanceDependencyMode(
      payrollSettings?.attendanceDependencyMode === "independent" ? "independent" : "based"
    );
    setIndependentAttendanceFeed(
      payrollSettings?.independentAttendanceFeed || "daywise_import"
    );
    const at = payrollSettings?.arrearsTreatmentJson || {};
    const cur = at.current && typeof at.current === "object" ? at.current : null;
    setArrearsMode(cur?.mode || "merge_single");
    setArrearsMergedName(cur?.mergedComponentName || "Arrears");
    setArrearsEffectiveFrom(
      cur?.effectiveFrom ||
        (() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        })()
    );
    setArrearsHistory(Array.isArray(at.history) ? at.history : []);
    const notify = payrollSettings?.payrollWorkflowJson?.finaliseNotify || {};
    setNotifyOnFinalise(notify.notifyOnFinalise === true);
    setNotifyOnUnfinalise(notify.notifyOnUnfinalise === true);
    setNotifyRoleCodes(
      Array.isArray(notify.roleCodes)
        ? notify.roleCodes.map((c) => String(c || "").trim()).filter(Boolean)
        : []
    );
  }, [payrollSettings]);

  const serviceCommenceDisplay = (() => {
    const raw = companyData?.serviceCommencesOn;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  })();

  const preservePayrollSettingsBody = (patch = {}) => ({
    isInvestmentEnabled: !!payrollSettings?.isInvestmentEnabled,
    isProofEnabled: !!payrollSettings?.isProofEnabled,
    isTaxRegimeLocked: payrollSettings?.isTaxRegimeLocked !== false,
    investmentWindows: payrollSettings?.investmentWindows ?? null,
    proofWindows: payrollSettings?.proofWindows ?? null,
    declarationWorkflow: payrollSettings?.declarationWorkflow ?? null,
    proofWorkflow: payrollSettings?.proofWorkflow ?? null,
    taxYear: payrollSettings?.taxYear ?? null,
    lockedTaxYears: payrollSettings?.lockedTaxYears ?? [],
    isTaxYearLocked: !!payrollSettings?.isTaxYearLocked,
    defaultTaxRegime: payrollSettings?.defaultTaxRegime ?? "NEW",
    investmentMode: payrollSettings?.investmentMode || "ALWAYS",
    proofMode: payrollSettings?.proofMode || "ALWAYS",
    payrollStepsJson: payrollSettings?.payrollStepsJson ?? null,
    payrollWorkflowJson: payrollSettings?.payrollWorkflowJson ?? null,
    allowMidMonthIncrements:
      payrollSettings?.allowMidMonthIncrements !== undefined
        ? !!payrollSettings.allowMidMonthIncrements
        : false,
    attendanceDependencyMode:
      payrollSettings?.attendanceDependencyMode === "independent"
        ? "independent"
        : "based",
    independentAttendanceFeed:
      payrollSettings?.independentAttendanceFeed || "daywise_import",
    ...patch,
  });

  const saveGeneralRow = async (rowKey, patch, successMessage) => {
    if (!canEditGeneral) {
      toast.error("You do not have permission to edit payroll settings.");
      return;
    }
    setSavingRow(rowKey);
    try {
      const response = await payrollService.upsertPayrollSettings(
        preservePayrollSettingsBody(patch)
      );
      if (response.success) {
        onSettingsSaved?.(response.data);
        toast.success(successMessage);
      } else {
        toast.error(response.message || "Failed to save settings");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to save payroll settings"
      );
    } finally {
      setSavingRow(null);
    }
  };

  const savedAllowMidMonth = !!payrollSettings?.allowMidMonthIncrements;
  const savedArrearsCur = payrollSettings?.arrearsTreatmentJson?.current || {};
  const savedArrearsMode = savedArrearsCur?.mode || "merge_single";
  const savedArrearsName = savedArrearsCur?.mergedComponentName || "Arrears";
  const savedArrearsFrom =
    savedArrearsCur?.effectiveFrom ||
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const savedAttendanceMode =
    payrollSettings?.attendanceDependencyMode === "independent" ? "independent" : "based";
  const savedAttendanceFeed = payrollSettings?.independentAttendanceFeed || "daywise_import";
  const savedNotify = payrollSettings?.payrollWorkflowJson?.finaliseNotify || {};
  const savedNotifyFinalise = savedNotify.notifyOnFinalise === true;
  const savedNotifyUnfinalise = savedNotify.notifyOnUnfinalise === true;
  const savedNotifyRoles = Array.isArray(savedNotify.roleCodes)
    ? [...savedNotify.roleCodes].map(String).sort().join("|")
    : "";
  const draftNotifyRoles = [...notifyRoleCodes].map(String).sort().join("|");

  const controlClass =
    "rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 py-1.5 text-sm disabled:opacity-50";

  const preserveDeclarationFields = (extra = {}) => ({
    isInvestmentEnabled: !!payrollSettings?.isInvestmentEnabled,
    isProofEnabled: !!payrollSettings?.isProofEnabled,
    isTaxRegimeLocked: payrollSettings?.isTaxRegimeLocked !== false,
    investmentWindows: payrollSettings?.investmentWindows ?? null,
    proofWindows: payrollSettings?.proofWindows ?? null,
    declarationWorkflow: payrollSettings?.declarationWorkflow ?? null,
    proofWorkflow: payrollSettings?.proofWorkflow ?? null,
    taxYear: payrollSettings?.taxYear ?? null,
    lockedTaxYears: payrollSettings?.lockedTaxYears ?? [],
    isTaxYearLocked: !!payrollSettings?.isTaxYearLocked,
    defaultTaxRegime: payrollSettings?.defaultTaxRegime ?? "NEW",
    investmentMode: payrollSettings?.investmentMode || "ALWAYS",
    proofMode: payrollSettings?.proofMode || "ALWAYS",
    allowMidMonthIncrements:
      payrollSettings?.allowMidMonthIncrements !== undefined
        ? !!payrollSettings.allowMidMonthIncrements
        : allowMidMonthIncrements,
    ...extra,
  });

  const saveReimbursements = async () => {
    if (!canEditReimbursements) {
      toast.error("You do not have permission to edit reimbursement settings.");
      return;
    }
    setSaving(true);
    try {
      const api = reimbRef.current;
      if (api?.validate) {
        const validation = api.validate();
        if (!validation.ok) {
          toast.error(validation.message || "Invalid Reimbursement settings");
          setSaving(false);
          return;
        }
      }
      const reimbPayload = api?.buildPayload?.() || {};
      const response = await payrollService.upsertPayrollSettings(
        preserveDeclarationFields(reimbPayload)
      );
      if (response.success) {
        onSettingsSaved?.(response.data);
        toast.success("Reimbursement settings saved");
      } else {
        toast.error(response.message || "Failed to save settings");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save reimbursement settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (!canEditGeneral) {
      toast.error("You do not have permission to edit payroll settings.");
      return;
    }
    setSaving(true);
    try {
      let declarationPayload = {};
      if (tab === "declaration" || invRef.current) {
        const api = invRef.current;
        if (api?.validate) {
          const validation = api.validate();
          if (!validation.ok) {
            toast.error(validation.message || "Invalid Declaration settings");
            setSaving(false);
            return;
          }
        }
        declarationPayload = api?.buildPayload?.() || {};
      }

      const settingsData = {
        ...declarationPayload,
        isInvestmentEnabled:
          declarationPayload.isInvestmentEnabled ??
          !!payrollSettings?.isInvestmentEnabled,
        isProofEnabled:
          declarationPayload.isProofEnabled ??
          !!payrollSettings?.isProofEnabled,
        isTaxRegimeLocked:
          declarationPayload.isTaxRegimeLocked ??
          payrollSettings?.isTaxRegimeLocked !== false,
        investmentWindows:
          declarationPayload.investmentWindows ??
          payrollSettings?.investmentWindows ??
          null,
        proofWindows:
          declarationPayload.proofWindows ??
          payrollSettings?.proofWindows ??
          null,
        declarationWorkflow:
          declarationPayload.declarationWorkflow ??
          payrollSettings?.declarationWorkflow ??
          null,
        proofWorkflow:
          declarationPayload.proofWorkflow ??
          payrollSettings?.proofWorkflow ??
          null,
        taxYear:
          declarationPayload.taxYear ?? payrollSettings?.taxYear ?? null,
        lockedTaxYears:
          declarationPayload.lockedTaxYears ??
          payrollSettings?.lockedTaxYears ??
          [],
        isTaxYearLocked:
          declarationPayload.isTaxYearLocked ??
          !!payrollSettings?.isTaxYearLocked,
        defaultTaxRegime:
          declarationPayload.defaultTaxRegime ??
          payrollSettings?.defaultTaxRegime ??
          "NEW",
        investmentMode: declarationPayload.investmentMode || "ALWAYS",
        proofMode: declarationPayload.proofMode || "ALWAYS",
        allowMidMonthIncrements:
          payrollSettings?.allowMidMonthIncrements !== undefined
            ? !!payrollSettings.allowMidMonthIncrements
            : allowMidMonthIncrements,
      };

      const response = await payrollService.upsertPayrollSettings(settingsData);
      if (response.success) {
        onSettingsSaved?.(response.data);
        toast.success("Payroll settings saved");
      } else {
        toast.error(response.message || "Failed to save settings");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save payroll settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = (id, label) =>
    tabAllowed[id] ? (
      <button
        key={id}
        type="button"
        onClick={() => setTab(id)}
        className={`-mb-px px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
          tab === id
            ? darkMode
              ? "border-b-2 border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-accent))]"
              : "border-b-2 border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]"
            : darkMode
            ? "border-b-2 border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-white"
            : "border-b-2 border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
        }`}
      >
        {label}
      </button>
    ) : null;

  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border ${
        darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
      }`}
    >
      <div className="px-5 pt-4 pb-0 dark:border-[hsl(var(--ds-border))]">
        <h2 className="text-xl font-bold mb-3">Payroll Settings</h2>
        <div className="flex flex-wrap items-center gap-1 border-b border-[hsl(var(--ds-border))] dark:border-[hsl(var(--ds-border))]">
          {tabBtn("general", "General Settings")}
          {tabBtn("salary_structures", "Salary Structures")}
          {tabBtn("statutory_rates_heads", "Statutory Rates and Reference Heads")}
          {tabBtn("declaration", "Declaration")}
          {tabBtn("reimbursements", "Reimbursements")}
          {tabBtn("policies", "Policies")}
          {tabBtn("payroll_setup", "Payroll Setup")}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {tab === "general" && canViewGeneral && (
          <div
            className={`rounded-2xl border p-4 space-y-5 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <div>
              <h3 className="text-lg font-semibold mb-3">General Settings</h3>
              {serviceCommenceDisplay ? (
                <div className="space-y-1 mb-5">
                  <label
                    className={`block text-sm font-medium ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    Service Commences On
                  </label>
                  <div
                    className={`inline-block border px-4 py-2 rounded-xl ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    {serviceCommenceDisplay}
                  </div>
                </div>
              ) : (
                <p className={`text-sm mb-5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Service commencement date not set.
                </p>
              )}

              <div
                className={`mb-5 rounded-xl border px-4 ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30"
                }`}
              >
                <EnterprisePayrollDefaultsRows canEdit={canEditGeneral} compact />

                <PayrollGeneralSettingRow
                  label="Allow mid month Increments"
                  infoTitle="When enabled, Salary Revision may use any calendar day as Increment Effective Date. When disabled, it is restricted to the 1st of the month."
                  dirty={allowMidMonthIncrements !== savedAllowMidMonth}
                  saving={savingRow === "midMonth"}
                  disabled={!canEditGeneral}
                  onSave={() =>
                    saveGeneralRow(
                      "midMonth",
                      { allowMidMonthIncrements },
                      "Mid-month increments setting saved"
                    )
                  }
                >
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                      checked={allowMidMonthIncrements}
                      disabled={!canEditGeneral || savingRow === "midMonth"}
                      onChange={(e) => setAllowMidMonthIncrements(e.target.checked)}
                    />
                    <span className="text-[hsl(var(--ds-muted-fg))]">Enabled</span>
                  </label>
                </PayrollGeneralSettingRow>

                <PayrollGeneralSettingRow
                  label="Arrears treatment (LOP reversal + salary revision)"
                  infoTitle="Controls how LOP reversal and salary-revision arrears appear in Process Payroll and registers from the effective pay month. Merge with single component = one renameable earning (default Arrears). Separate components = Arr. Basic / Arr. HRA / Arr. LOP Reversal etc. Merge with components = fold into current-month heads (e.g. Basic). Arr.PF and Arr.ESI are always one line each on the combined arrear wage. Prior versions are kept in history."
                  dirty={
                    String(arrearsMode) !== String(savedArrearsMode) ||
                    String(arrearsMergedName || "Arrears") !== String(savedArrearsName) ||
                    String(arrearsEffectiveFrom || "") !== String(savedArrearsFrom || "")
                  }
                  saving={savingRow === "arrears"}
                  disabled={!canEditGeneral}
                  onSave={() =>
                    saveGeneralRow(
                      "arrears",
                      {
                        arrearsTreatment: {
                          mode: arrearsMode,
                          mergedComponentName: arrearsMergedName,
                          effectiveFrom: arrearsEffectiveFrom,
                        },
                      },
                      "Arrears treatment saved"
                    )
                  }
                  footer={
                    arrearsHistory.length > 0 ? (
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">
                        <p className="mb-1 font-medium text-[hsl(var(--ds-fg))]">History</p>
                        <ul className="max-h-28 space-y-0.5 overflow-auto">
                          {arrearsHistory.map((h, i) => (
                            <li key={`${h.effectiveFrom || ""}-${i}`}>
                              {h.effectiveFrom || "—"} ·{" "}
                              {h.mode === "separate_components"
                                ? "Separate components"
                                : h.mode === "merge_components"
                                  ? "Merge with components"
                                  : `Single: ${h.mergedComponentName || "Arrears"}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null
                  }
                >
                  <select
                    value={arrearsMode}
                    disabled={!canEditGeneral || savingRow === "arrears"}
                    onChange={(e) => setArrearsMode(e.target.value)}
                    className={`${controlClass} min-w-[10.5rem]`}
                    aria-label="Arrears mode"
                  >
                    <option value="merge_single">Merge with single component</option>
                    <option value="separate_components">Separate components</option>
                    <option value="merge_components">Merge with components</option>
                  </select>
                  <input
                    type="text"
                    value={arrearsMergedName}
                    disabled={
                      !canEditGeneral ||
                      savingRow === "arrears" ||
                      arrearsMode !== "merge_single"
                    }
                    onChange={(e) => setArrearsMergedName(e.target.value)}
                    placeholder="Component name"
                    className={`${controlClass} min-w-[7rem] max-w-[9rem] disabled:opacity-50`}
                    aria-label="Merged component name"
                  />
                  <input
                    type="month"
                    value={arrearsEffectiveFrom}
                    disabled={!canEditGeneral || savingRow === "arrears"}
                    onChange={(e) => setArrearsEffectiveFrom(e.target.value)}
                    className={`${controlClass} min-w-[9.5rem]`}
                    aria-label="Effective from pay month"
                  />
                </PayrollGeneralSettingRow>

                <PayrollGeneralSettingRow
                  label="Attendance dependency for payroll"
                  infoTitle="Based: payroll requires attendance finalised/locked and uses day statuses. Independent: payroll can run without finalise; choose how Paid Days / LOP are supplied. Day-wise: Attendance Import without Finalise gate. Summary: Payroll Attendance Summary Excel. Either: summary preferred when uploaded. Full month: paid days = eligible, LOP = 0."
                  dirty={
                    attendanceDependencyMode !== savedAttendanceMode ||
                    (attendanceDependencyMode === "independent" &&
                      independentAttendanceFeed !== savedAttendanceFeed)
                  }
                  saving={savingRow === "attendance"}
                  disabled={!canEditGeneral}
                  onSave={() =>
                    saveGeneralRow(
                      "attendance",
                      {
                        attendanceDependencyMode,
                        independentAttendanceFeed,
                      },
                      "Attendance dependency saved"
                    )
                  }
                >
                  <select
                    value={attendanceDependencyMode}
                    disabled={!canEditGeneral || savingRow === "attendance"}
                    onChange={(e) => setAttendanceDependencyMode(e.target.value)}
                    className={`${controlClass} min-w-[10rem]`}
                    aria-label="Attendance dependency mode"
                  >
                    <option value="based">Attendance Based</option>
                    <option value="independent">Independent</option>
                  </select>
                  {attendanceDependencyMode === "independent" ? (
                    <select
                      value={independentAttendanceFeed}
                      disabled={!canEditGeneral || savingRow === "attendance"}
                      onChange={(e) => setIndependentAttendanceFeed(e.target.value)}
                      className={`${controlClass} min-w-[11rem]`}
                      aria-label="Independent attendance feed"
                    >
                      <option value="daywise_import">Day-wise Attendance Import</option>
                      <option value="summary_upload">Payroll summary upload</option>
                      <option value="either">Either (summary preferred)</option>
                      <option value="full_month">Full month (LOP 0)</option>
                    </select>
                  ) : null}
                </PayrollGeneralSettingRow>

                <PayrollGeneralSettingRow
                  label="Finalise / Unfinalise notifications"
                  infoTitle="Email mapped User Roles only — not all employees. Email failures do not block finalise or unlock."
                  dirty={
                    notifyOnFinalise !== savedNotifyFinalise ||
                    notifyOnUnfinalise !== savedNotifyUnfinalise ||
                    draftNotifyRoles !== savedNotifyRoles
                  }
                  saving={savingRow === "notify"}
                  disabled={!canEditGeneral}
                  onSave={() =>
                    saveGeneralRow(
                      "notify",
                      {
                        payrollWorkflowJson: {
                          ...(payrollSettings?.payrollWorkflowJson || {}),
                          finaliseNotify: {
                            notifyOnFinalise,
                            notifyOnUnfinalise,
                            roleCodes: notifyRoleCodes,
                          },
                        },
                      },
                      "Notification settings saved"
                    )
                  }
                  footer={
                    <div className="max-h-36 overflow-auto rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2">
                      <p className="mb-1.5 text-xs font-medium text-[hsl(var(--ds-fg))]">
                        Roles to notify
                      </p>
                      {policyRoleOptions.length === 0 ? (
                        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No roles loaded.</p>
                      ) : (
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          {policyRoleOptions.map((opt) => {
                            const checked = notifyRoleCodes.includes(opt.value);
                            return (
                              <label
                                key={opt.value}
                                className="inline-flex cursor-pointer items-center gap-2 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!canEditGeneral || savingRow === "notify"}
                                  onChange={() => {
                                    setNotifyRoleCodes((prev) =>
                                      prev.includes(opt.value)
                                        ? prev.filter((c) => c !== opt.value)
                                        : [...prev, opt.value]
                                    );
                                  }}
                                />
                                <span>{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  }
                >
                  <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm">
                    <input
                      type="checkbox"
                      checked={notifyOnFinalise}
                      disabled={!canEditGeneral || savingRow === "notify"}
                      onChange={(e) => setNotifyOnFinalise(e.target.checked)}
                    />
                    <span>On finalise</span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm">
                    <input
                      type="checkbox"
                      checked={notifyOnUnfinalise}
                      disabled={!canEditGeneral || savingRow === "notify"}
                      onChange={(e) => setNotifyOnUnfinalise(e.target.checked)}
                    />
                    <span>On unfinalise</span>
                  </label>
                </PayrollGeneralSettingRow>
              </div>
            </div>
          </div>
        )}

        {tab === "salary_structures" && canViewSalaryStructures && (
          <div className="min-h-[28rem] overflow-hidden">
            <EnhancedSalaryStructureManager
              darkMode={darkMode}
              employees={employees}
              departmentsList={departmentsList}
              branchesList={branchesList}
              designationsList={designationsList}
              subDepartmentsList={subDepartmentsList}
              gradesList={gradesList}
              categoriesList={categoriesList}
              variant="structuresOnly"
            />
          </div>
        )}

        {tab === "statutory_rates_heads" && canViewStatutory && (
          <StatutoryRatesHeadsPanel
            darkMode={darkMode}
            canEdit={canEdit}
            canViewTdsHeads={canViewTdsHeads}
            canViewTdsCompliance={canViewTdsCompliance}
            initialAct={initialAct}
          />
        )}

        <div className={tab === "declaration" && canViewDeclaration ? "block space-y-5" : "hidden"}>
          <InvestmentDeclarationSettings
            ref={invRef}
            darkMode={darkMode}
            employees={employees}
            settings={payrollSettings}
            onFlagsChange={onFlagsChange}
            canEdit={canEditGeneral}
            saving={saving}
            onSave={saveAll}
          />
        </div>

        <div
          className={
            tab === "reimbursements" && canViewReimbursements
              ? "block space-y-5"
              : "hidden"
          }
        >
          <ReimbursementSettings
            ref={reimbRef}
            darkMode={darkMode}
            settings={payrollSettings}
            canEdit={canEditReimbursements}
            saving={saving}
            onSave={saveReimbursements}
          />
        </div>

        {tab === "policies" && canViewPolicies && (
          <PayrollPoliciesTab
            darkMode={darkMode}
            canEdit={canEdit}
            branchesList={branchesList}
            departmentsList={departmentsList}
            designationsList={designationsList}
            gradesList={gradesList}
            categoriesList={categoriesList}
            subDepartmentsList={subDepartmentsList}
          />
        )}

        {tab === "payroll_setup" && canViewPayrollSetup && (
          <div className="space-y-5">
            <PayrollStepsSettingsTab
              darkMode={darkMode}
              payrollSettings={payrollSettings}
              canEdit={canEditGeneral}
              onSave={onSettingsSaved}
            />
          </div>
        )}
      </div>
    </div>
  );
}
